// components/Breadcrumbs.tsx (already created earlier)
import Link from 'next/link';
import { useRouter } from 'next/router';
import styles from './breadcrumbs.module.css';
import React from 'react';
import { toProperCase } from '../../helper/utils';
import { translate } from '../../helper/translate';
import { useBreadcrumbLabels } from '../../context/breadcrumbLabels';

interface BreadcrumbsProps {
  pathnames: string[];
  title?: string;
  isDark?: boolean;
}

const specialPageToQueryParam: Record<string, string> = {
  article: 'articles',
  'case-study': 'caseStudies',
  webinar: 'webinars',
  'white-paper': 'whitepapers',
  video: 'videos',
};

// Maps a resource-type url slug to its key in the translation dictionary so the
// resource-type crumb (e.g. "Webinar") localizes with the rest of the site.
const resourceTypeToTranslateKey: Record<string, string> = {
  article: 'articles',
  'case-study': 'case_studies',
  webinar: 'webinars',
  'white-paper': 'whitepapers',
  video: 'videos',
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ pathnames, title, isDark }) => {
  // Localized labels keyed by page url, sourced from the header navigation menu.
  const navLabels = useBreadcrumbLabels();
  const { locale } = useRouter();
  const activeLocale = locale || 'en-us';

  return (
    <div className={`${styles.breadcrumb} ${isDark ? styles.darkBackground : ''}`}>
      <span>
        <Link href="/" className={styles.breadcrumbLink}>
          {translate('home', activeLocale, 'Home')}
        </Link>
      </span>
      {pathnames.map((value, index) => {
        const pageName = value.toLowerCase();
        const isSpecialPage = pageName in specialPageToQueryParam;
        var cumulativePath = `/${pathnames.slice(0, index + 1).join('/')}`;
        const to = isSpecialPage
          ? `/resources?type=${specialPageToQueryParam[pageName]}`
          : cumulativePath;
        const isLast = index === pathnames.length - 1;
        const _cumulativePath = cumulativePath.replace(/\/+$/, '').toLowerCase();

        // Localized label from the CMS navigation menu (matched by cumulative path).
        var navLabel = navLabels?.[_cumulativePath];

        // Localized label for known resource types (webinar, article, etc.).
        const resourceTypeKey = resourceTypeToTranslateKey[pageName];
        const typeLabel = resourceTypeKey
          ? translate(resourceTypeKey, activeLocale, toProperCase(value))
          : undefined;

        // Resolution order: page title (last crumb only) -> CMS nav label ->
        // resource-type translation -> prettified url slug.
        const fallback = toProperCase(value.split(/[?#]/)[0]);
        const label = navLabel || typeLabel || fallback;

        return (
          <React.Fragment key={to}>
            <span className={styles.currentBreadcrumb}>&nbsp;/&nbsp;</span>
            {isLast ? (
              <span className={styles.currentBreadcrumb} aria-current="page">
                {title || label}
              </span>
            ) : (
              <Link href={to} className={styles.breadcrumbLink}>
                {label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
